import DOMPurify from "dompurify";
import {
  AlertCircle,
  Bus,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  FileText,
  Info,
  MapPin,
  Mountain,
  Route,
  Star,
  TreePine,
  X,
} from "lucide-react";
import { refundPolicyTiers, specialRefundCases } from "@/data/refundPolicyData";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// 기본 이미지로 webp 형식을 사용하여 성능 최적화
const heroImage = "/images/image1.webp";
import Footer from "@/components/Footer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BackButton } from "@/components/ui/back-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";
import { useProductDetailData } from "@/hooks/useProductDetailData";
import { ProductDetailErrorBoundary } from "@/components/ProductDetailErrorBoundary";

const ProductDetail = () => {
  const getDifficultyStars = (difficulty: string | null) => {
    const level = difficulty === "쉬움" ? 1 : difficulty === "어려움" ? 3 : 2;
    return Array.from({ length: 3 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < level
            ? "fill-[#F39800] text-[#F39800]"
            : "fill-gray-200 text-gray-200"
        }`}
      />
    ));
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}시간`;
    return `${hours}시간 ${mins}분`;
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    schedule: true,
    highlight: true,
    description: true,
  });

  // React Query를 사용한 데이터 페칭
  const { data, isLoading, error } = useProductDetailData(id);
  const product = data?.product;
  const course = data?.course;
  const trail = data?.trail;

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 뒤로가기 핸들러 - 코스 목록으로 이동
  const handleGoBack = () => {
    navigate('/courses');
  };

  const handleBooking = () => {
    if (product) {
      navigate(`/booking/new/${product.product_id}`);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F39800] mx-auto mb-4"></div>
          <p className="text-gray-600">상품 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button 
            onClick={() => navigate('/courses')} 
            className="bg-[#F39800] hover:bg-[#E08700]"
          >
            코스 목록으로 돌아가기
          </Button>
        </div>
      </div>
    );
  }

  if (!product || !course) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-[#F39800]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <TreePine className="w-10 h-10 text-[#F39800]" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              상품 준비 중입니다
            </h2>
            <Button
              onClick={() => navigate("/courses")}
              className="w-full bg-[#F39800] hover:bg-[#E08700] text-white"
            >
              다른 코스 둘러보기
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Product image
  const productImage =
    course?.main_photo_url || product?.main_photo_url || heroImage;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header - Back Button + Title */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <BackButton onClick={handleGoBack} showText={true}>
              뒤로
            </BackButton>
            <h1 className="text-lg font-bold text-gray-800 text-center flex-1 mx-4 truncate">
              {product.name}
            </h1>
            <div className="w-16" /> {/* Spacer for center alignment */}
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Desktop/Tablet Layout - 2 Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
          {/* Left Column - Main Content (2/3 width on desktop) */}
          <div className="md:col-span-2 space-y-6">
            {/* Product Image */}
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg">
              <div className="relative aspect-[16/10]">
                <img
                  src={productImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/90 backdrop-blur-sm text-gray-800 shadow-sm">
                    {trail?.name || "코리아둘레길"}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Product Header - Only shown on mobile */}
            {isMobile && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-[#F39800]">
                        {product.price.toLocaleString()}원
                      </span>
                      <span className="text-sm text-gray-500">/인</span>
                    </div>
                    <h2 className="text-lg font-bold text-gray-800 mt-1">
                      {course.name}
                    </h2>
                  </div>
                  {course.recommend_count > 0 && (
                    <Badge className="bg-[#F39800]/10 text-[#F39800] text-xs">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      {course.recommend_count}
                    </Badge>
                  )}
                </div>

                {/* Quick Info Grid */}
                <div className="grid grid-cols-4 gap-2 py-3 border-y">
                  <div className="text-center">
                    <Route className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">거리</p>
                    <p className="text-xs font-semibold">{course.length}km</p>
                  </div>
                  <div className="text-center">
                    <Clock className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">소요</p>
                    <p className="text-xs font-semibold">
                      {formatDuration(course.estimated_time)}
                    </p>
                  </div>
                  <div className="text-center">
                    <Mountain className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">난이도</p>
                    <div className="flex justify-center gap-0.5">
                      {getDifficultyStars(course.difficulty)}
                    </div>
                  </div>
                </div>

                {/* Route Info */}
                <div className="mt-3">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">구간</p>
                      <p className="text-sm font-medium break-words">
                        {course.start_point} → {course.end_point}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Sections with Collapsible */}
            <div className="space-y-3">
              {/* Product Description Section */}
              {product.description && (
                <Collapsible
                  open={expandedSections.description}
                  onOpenChange={() => toggleSection("description")}
                >
                  <Card className="shadow-sm">
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="py-4">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[#F39800]" />
                            상품 설명
                          </CardTitle>
                          {expandedSections.description ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(product.description, {
                              ALLOWED_TAGS: [
                                "p",
                                "br",
                                "strong",
                                "em",
                                "u",
                                "strike",
                                "ul",
                                "ol",
                                "li",
                                "a",
                                "h1",
                                "h2",
                                "h3",
                                "h4",
                                "h5",
                                "h6",
                              ],
                              ALLOWED_ATTR: ["href", "target", "rel"],
                              ADD_ATTR: ["target", "rel"],
                              FORCE_BODY: true,
                            }),
                          }}
                        />
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )}

              {/* Schedule Section */}
              <Collapsible
                open={expandedSections.schedule}
                onOpenChange={() => toggleSection("schedule")}
              >
                <Card className="shadow-sm">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Clock className="w-5 h-5 text-[#F39800]" />
                          일정 안내
                        </CardTitle>
                        {expandedSections.schedule ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        {(() => {
                          const template = product?.schedule_template;

                          // 새로운 구조 체크
                          if (
                            template?.schedule_items &&
                            Array.isArray(template.schedule_items)
                          ) {
                            // 새로운 schedule_items 배열 구조
                            return template.schedule_items.map(
                              (item, index) => (
                                <div
                                  key={index}
                                  className="flex items-start gap-3"
                                >
                                  <div
                                    className={`w-2 h-2 rounded-full mt-2 ${
                                      index === 0 ||
                                      index ===
                                        template.schedule_items.length - 1
                                        ? "bg-[#F39800]"
                                        : "bg-gray-300"
                                    }`}
                                  ></div>
                                  <div className="flex-1">
                                    <p className="font-medium">
                                      {item.time || "-"}
                                    </p>
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="text-sm text-gray-600">
                                          {item.location || "-"}
                                        </p>
                                      </div>
                                      {item.map_link && (
                                        <a
                                          href={item.map_link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="ml-2 text-[#F39800] hover:text-[#E08700] transition-colors"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            );
                          } else {
                            // 구버전 또는 데이터 없음 - 기본 일정 표시
                            const defaultStops = [
                              {
                                time: "08:00",
                                location: "사당역 6번 출구 우리은행 앞",
                                map_link:
                                  "https://map.kakao.com/link/map/37.476559,126.981654",
                              },
                              {
                                time: "09:30",
                                location:
                                  course?.start_point || "트레킹 시작점",
                                map_link: null,
                              },
                              {
                                time: "16:30",
                                location: course?.end_point || "트레킹 종점",
                                map_link: null,
                              },
                              {
                                time: "20:00",
                                location: "사당역 6번 출구 우리은행 앞",
                                map_link:
                                  "https://map.kakao.com/link/map/37.476559,126.981654",
                              },
                            ];

                            return (
                              <>
                                {defaultStops.map((stop, index) => (
                                  <div
                                    key={index}
                                    className="flex items-start gap-3"
                                  >
                                    <div
                                      className={`w-2 h-2 rounded-full mt-2 ${
                                        index === 0 ||
                                        index === defaultStops.length - 1
                                          ? "bg-[#F39800]"
                                          : "bg-gray-300"
                                      }`}
                                    ></div>
                                    <div className="flex-1">
                                      <p className="font-medium">{stop.time}</p>
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <p className="text-sm text-gray-600">
                                            {stop.location}
                                          </p>
                                        </div>
                                        {stop.map_link && (
                                          <a
                                            href={stop.map_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-2 text-[#F39800] hover:text-[#E08700] transition-colors"
                                          >
                                            <ExternalLink className="w-4 h-4" />
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {course?.estimated_time && (
                                  <div className="mt-2 text-xs text-gray-500 pl-5">
                                    * 트레킹 예상 소요시간:{" "}
                                    {formatDuration(course.estimated_time)}
                                  </div>
                                )}
                              </>
                            );
                          }
                        })()}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Highlights Section */}
              {course.tour_info && (
                <Collapsible
                  open={expandedSections.highlight}
                  onOpenChange={() => toggleSection("highlight")}
                >
                  <Card className="shadow-sm">
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="py-4">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-[#F39800]" />
                            코스 하이라이트
                          </CardTitle>
                          {expandedSections.highlight ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {course.tour_info.split("\n").map(
                            (line: string, index: number) =>
                              line.trim() && (
                                <div
                                  key={index}
                                  className="flex items-start gap-2"
                                >
                                  <CheckCircle2 className="w-4 h-4 text-[#F39800] flex-shrink-0 mt-0.5" />
                                  <p className="text-sm text-gray-700">
                                    {line.replace(/^-\s*/, "")}
                                  </p>
                                </div>
                              )
                          )}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )}

              {/* Included/Not Included */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="shadow-sm">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm text-green-700 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      포함사항
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="text-sm space-y-1 text-gray-700">
                      <li>• 왕복 교통비</li>
                      <li>• 전문 인솔자</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm text-red-700 flex items-center gap-1">
                      <X className="w-4 h-4" />
                      불포함사항
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="text-sm space-y-1 text-gray-700">
                      <li>• 개인 식사비</li>
                      <li>• 입장료</li>
                      <li>• 개인 경비</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Important Notices */}
            <div className="space-y-3">
              {/* Precautions */}
              <Alert className="border-amber-200 bg-amber-50 shadow-sm">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-900">유의사항</AlertTitle>
                <AlertDescription className="mt-2">
                  <div className="space-y-1 text-sm text-amber-800">
                    <p>
                      • 급작스러운 상황(기상악화, 탐방로통제)으로 인한
                      코스변경이 있을 수 있으며, 그에 따른 정상 인증 불가는
                      환불의 사유가 될 수 없습니다.
                    </p>
                    <p>• 식사, 간식 및 식수는 개인이 지참하여야 합니다.</p>
                    <p>
                      • 이용자 분들의 휴식을 위해 차내에서 대화, 취식은 자제해
                      주시기 바랍니다.
                    </p>
                    <p>
                      • 안전 운행 보장 및 타 이용자 휴식 방해 우려가 있을 경우,
                      홀씨트레킹 탑승이 거부될 수 있습니다. (예 : 과음 상태)
                    </p>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Cancellation Policy */}
              <Alert className="border-red-200 bg-red-50 shadow-sm">
                <Info className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-900">환불 규정</AlertTitle>
                <AlertDescription className="mt-2">
                  <div className="font-semibold mb-1">환불 정책</div>
                  {refundPolicyTiers.map((tier) => (
                    <p key={tier.id}>
                      • <strong>{tier.refundPercentage}% 환불</strong>: {tier.condition}
                    </p>
                  ))}
                  <div className="mt-2 p-2 bg-red-100 rounded">
                    <p className="text-xs">
                      ※ {specialRefundCases.weather.note}
                    </p>
                  </div>
                  <div className="mt-3 pt-2 border-t border-red-200">
                    <p className="font-semibold mb-1">
                      여행사에 의한 여행 취소 규정
                    </p>
                    <p>
                      • {specialRefundCases.companyCancel.description}
                    </p>
                    <p>
                      • {specialRefundCases.minimumParticipants.description}
                    </p>
                    <p>
                      • {specialRefundCases.companyCancel.note}
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          </div>

          {/* Right Column - Sticky Sidebar (1/3 width on desktop) */}
          {!isMobile && (
            <div className="space-y-6">
              {/* Sticky Action Card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-20">
                {/* Price and Title */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold text-[#F39800]">
                      {product.price.toLocaleString()}원
                    </span>
                    <span className="text-sm text-gray-500">/인</span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {course.name}
                  </h2>
                  {course.recommend_count > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Star className="w-4 h-4 fill-[#F39800] text-[#F39800]" />
                      <span className="text-sm text-gray-600">
                        {course.recommend_count}명 추천
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick Info */}
                <div className="space-y-3 mb-6 pb-6 border-b">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">거리</span>
                    <span className="font-semibold">{course.length}km</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">소요시간</span>
                    <span className="font-semibold">
                      {formatDuration(course.estimated_time)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">난이도</span>
                    <div className="flex gap-0.5">
                      {getDifficultyStars(course.difficulty)}
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={handleBooking}
                  className="w-full bg-[#F39800] hover:bg-[#E08700] text-white py-3 text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                  size="lg"
                >
                  예약하기
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t shadow-lg z-50">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-600">1인</p>
                <p className="text-lg font-bold text-[#F39800]">
                  {product.price.toLocaleString()}원
                </p>
              </div>
              <Button
                onClick={handleBooking}
                className="bg-[#F39800] hover:bg-[#E08700] text-white px-8 py-6 text-lg font-semibold"
              >
                예약하기
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add padding bottom on mobile to account for sticky bar + safe area */}
      <div className={isMobile ? "pb-mobile-nav" : ""}>
        {/* Empty div for spacing */}
      </div>
      <Footer />
    </div>
  );
};

// Wrap ProductDetail with Error Boundary to catch and handle errors gracefully
const ProductDetailWithErrorBoundary = () => (
  <ProductDetailErrorBoundary>
    <ProductDetail />
  </ProductDetailErrorBoundary>
);

export default ProductDetailWithErrorBoundary;
